import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/Loading";
import { Stepper, WIZARD_STEPS } from "@/components/wizard/Stepper";
import { StepVideo } from "@/components/wizard/StepVideo";
import { StepDetails } from "@/components/wizard/StepDetails";
import { StepSticker } from "@/components/wizard/StepSticker";
import { StepPreview } from "@/components/wizard/StepPreview";
import { StepProducts, needsDesign } from "@/components/wizard/StepProducts";
import { StepShipping } from "@/components/wizard/StepShipping";
import { StepPay } from "@/components/wizard/StepPay";

const SignedInAs = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const switchAccount = async () => {
    await logout();
    navigate("/login?next=/create", { replace: true });
  };
  return (
    <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 pt-6 text-base text-[#5C4A3E] sm:px-8" data-testid="signed-in-as">
      <span>Signed in as <b className="text-[#2C1E16]">{user?.name || user?.email}</b></span>
      <button className="font-semibold text-[#A83232] underline underline-offset-4" onClick={switchAccount} data-testid="switch-account-button">Not you? Use a different account</button>
    </div>
  );
};

const STEP_COMPONENTS = [StepVideo, StepDetails, StepProducts, StepSticker, StepPreview, StepShipping, StepPay];

const StartOrResume = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState(null);
  const start = useCallback(async () => {
    const { data } = await api.post("/orders");
    navigate(`/create/${data.order_id}`, { replace: true });
  }, [navigate]);
  useEffect(() => {
    api.get("/orders").then(({ data }) => {
      const d = data.filter((o) => ["draft", "pending_payment"].includes(o.status));
      if (d.length) setDrafts(d);
      else start().catch((e) => toast.error(errMsg(e)));
    });
  }, [start]);
  if (!drafts) return <Loading text="Getting things ready..." />;
  return (
    <div className="mx-auto max-w-2xl px-4 py-12" data-testid="resume-choice">
      <h1 className="text-4xl font-bold text-[#4A2A18]">Welcome back!</h1>
      <p className="mt-3 text-lg text-[#5C4A3E]">You have a keepsake you haven't finished. Would you like to continue it?</p>
      <div className="mt-8 space-y-4">
        {drafts.map((d) => (
          <button key={d.order_id} className="paper-card flex w-full items-center justify-between p-5 text-left transition-colors hover:border-[#A83232]" onClick={() => navigate(`/create/${d.order_id}`)} data-testid={`resume-draft-${d.order_id}`}>
            <span><span className="block font-serif text-2xl font-semibold">{d.title || "Untitled keepsake"}</span><span className="text-[#5C4A3E]">Step {d.step} of {WIZARD_STEPS.length}</span></span>
            <span className="btn-outline">Continue</span>
          </button>
        ))}
        <button className="btn-secondary w-full" onClick={start} data-testid="start-new-keepsake-button">Start a new keepsake instead</button>
      </div>
    </div>
  );
};

export default function Create() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}`).then(({ data }) => {
      if (!["draft", "pending_payment"].includes(data.status)) return navigate(`/orders/${orderId}`, { replace: true });
      setOrder(data);
      setStep(params.get("canceled") ? WIZARD_STEPS.length : data.step || 1);
    }).catch(() => navigate("/orders", { replace: true }));
    if (params.get("canceled")) toast.info("Payment was canceled. Your keepsake is saved - you can pay whenever you're ready.");
  }, [orderId, navigate, params]);

  if (!orderId) return <><SignedInAs /><StartOrResume /></>;
  if (!order) return <Loading />;

  const go = async (n, patch = {}) => {
    try {
      const { data } = await api.patch(`/orders/${orderId}`, { ...patch, step: Math.max(n, order.step || 1) });
      setOrder(data);
      setStep(n);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const Step = STEP_COMPONENTS[step - 1];
  return (
    <>
    <SignedInAs />
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8 sm:py-12" data-testid="create-wizard">
      <Stepper step={step} maxStep={order.step || 1} skip={needsDesign(order) ? 0 : 4} onJump={(n) => setStep(n)} />
      <section className="paper-card fade-up mt-8 p-5 sm:p-10" key={step} data-testid={`wizard-step-${step}`}>
        <p className="font-script text-2xl text-[#A83232]">Step {step} of {WIZARD_STEPS.length}</p>
        <Step order={order} setOrder={setOrder} go={go} back={() => setStep(step === 5 && !needsDesign(order) ? 3 : step - 1)} />
      </section>
      <p className="mt-6 text-center text-base text-[#5C4A3E]" data-testid="autosave-note">Your progress is saved automatically. You can close this page and come back later.</p>
    </div>
    </>
  );
}
